import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import ConfirmDialog from '../../../components/ui/ConfirmDialog';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';

const AccountProfile = ({ user, onUpdate, onSuccess }) => {
  const navigate = useNavigate();
  const { user: authUser, updateProfile } = useAuth();
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [deleteConfirmationText, setDeleteConfirmationText] = useState('');

  const isDeletePhraseValid = deleteConfirmationText?.trim() === 'DELETE';

  // Pre-fill form with real user data when it arrives
  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        name: user?.name || '',
        email: user?.email || ''
      }));
      setAvatarUrl(user?.avatar || null);
    }
  }, [user?.name, user?.email, user?.avatar]);

  const validateEmail = (value) => {
    if (!value) return 'Email is required';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex?.test(value)) return 'Please enter a valid email address';
    return '';
  };

  const validateField = (name, value) => {
    switch (name) {
      case 'name':
        return !value ? 'Name is required' : '';
      case 'email':
        return validateEmail(value);
      case 'newPassword':
        if (value && value?.length < 8) return 'Password must be at least 8 characters';
        return '';
      case 'confirmPassword':
        if (value && value !== formData?.newPassword) return 'Passwords do not match';
        return '';
      default:
        return '';
    }
  };

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (touched?.[name]) {
      setErrors(prev => ({ ...prev, [name]: validateField(name, value) }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e?.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    setErrors(prev => ({ ...prev, [name]: validateField(name, value) }));
  };

  const handleAvatarChange = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file || !authUser?.id) return;

    const maxSize = 2 * 1024 * 1024;
    if (file?.size > maxSize) {
      setErrors(prev => ({ ...prev, avatar: 'Image must be under 2MB' }));
      return;
    }

    setAvatarUploading(true);
    setErrors(prev => ({ ...prev, avatar: '' }));

    try {
      const fileExt = file?.name?.split('.')?.pop();
      const filePath = `avatars/${authUser?.id}.${fileExt}`;

      const { error: uploadError } = await supabase?.storage?.from('avatars')?.upload(filePath, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase?.storage?.from('avatars')?.getPublicUrl(filePath);

      setAvatarUrl(publicUrl);
      await updateProfile({ avatar_url: publicUrl });
      onSuccess?.('Profile photo updated');
    } catch (err) {
      // If storage bucket doesn't exist, just show a friendly message
      setErrors(prev => ({ ...prev, avatar: 'Photo upload unavailable. Please try again later.' }));
    } finally {
      setAvatarUploading(false);
    }
  };

  const handleSaveProfile = async () => {
    const newErrors = {};
    ['name', 'email']?.forEach(key => {
      const error = validateField(key, formData?.[key]);
      if (error) newErrors[key] = error;
    });

    if (Object.keys(newErrors)?.length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsLoading(true);
    try {
      const { error } = await updateProfile({
        full_name: formData?.name,
        email: formData?.email
      });

      if (error) {
        setErrors(prev => ({ ...prev, name: error?.message || 'Failed to update profile' }));
      } else {
        onUpdate?.({ name: formData?.name, email: formData?.email });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangePassword = async () => {
    const newErrors = {};
    if (!formData?.currentPassword) newErrors.currentPassword = 'Current password is required';
    if (!formData?.newPassword) newErrors.newPassword = 'New password is required';
    if (!formData?.confirmPassword) newErrors.confirmPassword = 'Please confirm your password';
    
    const passwordError = validateField('newPassword', formData?.newPassword);
    if (passwordError) newErrors.newPassword = passwordError;
    
    const confirmError = validateField('confirmPassword', formData?.confirmPassword);
    if (confirmError) newErrors.confirmPassword = confirmError;

    if (Object.keys(newErrors)?.length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsLoading(true);
    const { error } = await supabase?.auth?.updateUser({ password: formData?.newPassword });
    setIsLoading(false);

    if (error) {
      setErrors(prev => ({ ...prev, newPassword: error?.message || 'Failed to update password. Please try again.' }));
    } else {
      setFormData(prev => ({
        ...prev,
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      }));
      onSuccess?.('Password changed successfully');
    }
  };

  const handleDeleteAccount = async () => {
    if (!isDeletePhraseValid) return;
    setShowDeleteConfirm(false);
    setDeleteError('');
    setIsLoading(true);

    try {
      const { error } = await supabase.functions.invoke('delete-account');
      if (error) throw error;

      // Clear local session state after account removal.
      await supabase.auth.signOut();
      onSuccess?.('Account deleted successfully');
      navigate('/', { replace: true });
    } catch (err) {
      const message = err?.message || 'Failed to delete account. Please try again.';
      setDeleteError(message);
    } finally {
      setIsLoading(false);
      setDeleteConfirmationText('');
    }
  };

  const initials = formData?.name
    ? formData?.name?.split(' ')?.map(n => n?.[0])?.join('')?.toUpperCase()?.slice(0, 2)
    : (user?.email?.[0] || '?')?.toUpperCase();

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Profile Information */}
      <div>
        <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4 flex items-center gap-2">
          <Icon name="User" size={20} color="#9333EA" />
          Profile Information
        </h2>
        <div className="space-y-4 sm:space-y-5">
          {/* Profile Photo */}
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4">
            <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-gradient-to-r from-purple-600 to-cyan-500 flex items-center justify-center text-white text-2xl font-bold flex-shrink-0 overflow-hidden">
              {avatarUrl ? (
                <img src={avatarUrl} alt="Profile avatar" className="w-full h-full object-cover" />
              ) : (
                initials
              )}
            </div>
            <div className="text-center sm:text-left">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif"
                className="hidden"
                onChange={handleAvatarChange}
              />
              <Button
                variant="outline"
                size="sm"
                iconName={avatarUploading ? 'Loader' : 'Upload'}
                className="min-h-[44px]"
                onClick={() => fileInputRef?.current?.click()}
                disabled={avatarUploading}
              >
                {avatarUploading ? 'Uploading...' : 'Change Photo'}
              </Button>
              <p className="text-xs text-gray-500 mt-2">JPG, PNG or GIF. Max 2MB.</p>
              {errors?.avatar && <p className="text-xs text-red-500 mt-1">{errors?.avatar}</p>}
            </div>
          </div>

          <Input
            label="Full Name"
            name="name"
            value={formData?.name}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors?.name}
            required
            className="min-h-[44px]"
          />

          <Input
            label="Email Address"
            name="email"
            type="email"
            value={formData?.email}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors?.email}
            required
            className="min-h-[44px]"
          />

          <Button
            onClick={handleSaveProfile}
            loading={isLoading}
            iconName="Save"
            className="w-full sm:w-auto min-h-[44px]"
          >
            Save Profile
          </Button>
        </div>
      </div>
      {/* Security Settings */}
      <div className="border-t pt-6 sm:pt-8">
        <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4 flex items-center gap-2">
          <Icon name="Lock" size={20} color="#9333EA" />
          Security Settings
        </h2>
        <div className="space-y-4 sm:space-y-5">
          <Input
            label="Current Password"
            name="currentPassword"
            type="password"
            value={formData?.currentPassword}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors?.currentPassword}
            placeholder="Enter current password"
            className="min-h-[44px]"
          />

          <Input
            label="New Password"
            name="newPassword"
            type="password"
            value={formData?.newPassword}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors?.newPassword}
            placeholder="Enter new password"
            description="Must be at least 8 characters"
            className="min-h-[44px]"
          />

          <Input
            label="Confirm New Password"
            name="confirmPassword"
            type="password"
            value={formData?.confirmPassword}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors?.confirmPassword}
            placeholder="Confirm new password"
            className="min-h-[44px]"
          />

          <Button
            onClick={handleChangePassword}
            loading={isLoading}
            variant="outline"
            iconName="Key"
            className="w-full sm:w-auto min-h-[44px]"
          >
            Change Password
          </Button>
        </div>
      </div>
      {/* Two-Factor Authentication */}
      <div className="border-t pt-6 sm:pt-8">
        <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4 flex items-center gap-2">
          <Icon name="Shield" size={20} color="#9333EA" />
          Two-Factor Authentication
        </h2>
        
        <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start gap-3">
            <Icon name="Info" size={20} color="#2563EB" className="flex-shrink-0 mt-0.5" />
            <div className="text-sm text-gray-700 space-y-2">
              <p>Two-factor authentication adds an extra layer of security to your account by requiring a verification code in addition to your password.</p>
            </div>
          </div>
        </div>
      </div>
      {/* Danger Zone */}
      <div className="border-t pt-6 sm:pt-8">
        <h2 className="text-lg sm:text-xl font-bold text-red-600 mb-3 sm:mb-4 flex items-center gap-2">
          <Icon name="AlertTriangle" size={20} color="#DC2626" />
          Danger Zone
        </h2>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm text-red-700 mb-3">
            Once you delete your account, there is no going back. Please be certain.
          </p>
          {deleteError && (
            <p className="text-sm text-red-600 mb-3">{deleteError}</p>
          )}
          <Button
            variant="outline"
            onClick={() => setShowDeleteConfirm(true)}
            iconName="Trash2"
            className="border-red-300 text-red-600 hover:bg-red-50 min-h-[44px]"
            loading={isLoading}
            disabled={isLoading}
          >
            Delete Account
          </Button>
        </div>
      </div>
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setDeleteConfirmationText('');
        }}
        onConfirm={handleDeleteAccount}
        title="Delete Account"
        message="Are you sure you want to delete your account? This action cannot be undone and all your data will be permanently removed."
        confirmText="Delete Account"
        cancelText="Cancel"
        variant="danger"
        confirmDisabled={!isDeletePhraseValid || isLoading}
        confirmLoading={isLoading}
      >
        <div className="space-y-2">
          <p className="text-sm text-gray-700 text-center">
            Type <span className="font-semibold">DELETE</span> to confirm.
          </p>
          <input
            type="text"
            value={deleteConfirmationText}
            onChange={(e) => setDeleteConfirmationText(e?.target?.value || '')}
            placeholder="Type DELETE"
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-100"
          />
        </div>
      </ConfirmDialog>
    </div>
  );
};

export default AccountProfile;